import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  Download, 
  Bell,
  FileText,
  Settings,
  LogOut,
  Home,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  Phone,
  ExternalLink,
  X,
  Star,
  Send,
  Menu
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { getProducts, Product } from '../services/productService';
import { saveProductRequest, getCustomerProducts, ProductRequest } from '../services/customerService';
import { uploadDocument, getCustomerDocuments, Document, formatFileSize, deleteDocument } from '../services/documentService';
import { CARD_BRANDS, getRateOffers, parseRateHistory, QuoteRequest, requestAnotherRate, respondToQuoteRate } from '../services/quoteService';
import RateHistory from '../components/RateHistory';
import { downloadRatePdf } from '../utils/ratePdf';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { logger } from '../utils/logger';
import { useToast } from '../components/Toast';

const CustomerDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showProductRequestModal, setShowProductRequestModal] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<any>(null);
  const [manageRequest, setManageRequest] = useState({
    type: '',
    description: '',
    urgency: 'normal'
  });
  const [customerProducts, setCustomerProducts] = useState<(Product & { assignedAt: Date })[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [userData, setUserData] = useState<{ firstName: string; lastName: string; email: string; phone: string; businessName: string; createdAt?: Date } | null>(null);
  const [productRequests, setProductRequests] = useState<ProductRequest[]>([]);
  const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [respondingQuoteId, setRespondingQuoteId] = useState<string | null>(null);
  const [declineQuote, setDeclineQuote] = useState<QuoteRequest | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentDescription, setDocumentDescription] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  // Load user data from Firestore
  useEffect(() => {
    const loadUserData = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || currentUser.email || '',
            phone: data.phone || '',
            businessName: data.businessName || '',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined
          });
        } else {
          // Fallback to auth data
          setUserData({
            firstName: currentUser.displayName?.split(' ')[0] || '',
            lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
            email: currentUser.email || '',
            phone: '',
            businessName: ''
          });
        }
      } catch (error) {
        logger.error('Error loading user data:', error);
        // Fallback to auth data
        const currentUser = auth.currentUser;
        if (currentUser) {
          setUserData({
            firstName: currentUser.displayName?.split(' ')[0] || '',
            lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
            email: currentUser.email || '',
            phone: '',
            businessName: ''
          });
        }
      }
    };
    
    loadUserData();
  }, []);

  // Load customer products from Firestore
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsLoadingProducts(false);
      return;
    }

    logger.log('🎯 Customer Dashboard: Setting up real-time listener for user:', {
      uid: currentUser.uid,
      email: currentUser.email,
      displayName: currentUser.displayName
    });

    setIsLoadingProducts(true);

    // Set up real-time listener for customer products (assigned products)
    const customerProductsQuery = query(
      collection(db, 'customerProducts'),
      where('customerId', '==', currentUser.uid)
    );

    // Set up real-time listener for all products (product catalog)
    const allProductsQuery = query(collection(db, 'products'));

    let allProducts: Product[] = [];

    const unsubscribeAllProducts = onSnapshot(allProductsQuery, (snapshot) => {
      try {
        logger.log('🔄 Customer Dashboard: Product catalog update received!');
        logger.log('📊 Product catalog snapshot:', snapshot.docs.length, 'documents');

        allProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
        logger.log('📦 Updated product catalog:', allProducts.length, 'products');
        setAvailableProducts(allProducts);
      } catch (error) {
        logger.error('❌ Error processing product catalog update:', error);
      }
    }, (error) => {
      logger.error('❌ Product catalog listener error:', error);
    });

    const unsubscribeCustomerProducts = onSnapshot(customerProductsQuery, async (snapshot) => {
      try {
        logger.log('🔄 Customer Dashboard: Real-time update received!');
        logger.log('📊 Customer products snapshot:', snapshot.docs.length, 'documents');

        // Filter for active products only and sort by assignedAt (newest first)
        const activeAssignments = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as any))
          .filter((assignment: any) => assignment.status === 'active' || assignment.status === true)
          .sort((a: any, b: any) => {
            const aDate = a.assignedAt?.toDate?.() || new Date(0);
            const bDate = b.assignedAt?.toDate?.() || new Date(0);
            return bDate.getTime() - aDate.getTime(); // Newest first
          });

        logger.log('✅ Active assignments:', activeAssignments.length);

        if (activeAssignments.length > 0) {
          // Map assigned products to full product details and remove duplicates
          const customerProductsWithDetails = activeAssignments.map((assignment: any) => {
            const product = allProducts.find(p => p.id === assignment.productId);
            return product ? { 
              ...product, 
              assignedAt: assignment.assignedAt?.toDate() || new Date(), 
              assignmentId: assignment.id 
            } : null;
          }).filter(Boolean) as (Product & { assignedAt: Date; assignmentId: string })[];
          
          // Remove duplicate products (keep only the latest assignment)
          const uniqueProducts = customerProductsWithDetails.filter((product, index, self) => 
            index === self.findIndex(p => p.id === product.id)
          );
          
          logger.log('🎯 Final unique products:', uniqueProducts.length);
          setCustomerProducts(uniqueProducts);
        } else {
          logger.log('📭 No active products found');
          setCustomerProducts([]);
        }
      } catch (error) {
        logger.error('❌ Error processing real-time update:', error);
      } finally {
        setIsLoadingProducts(false);
      }
    }, (error) => {
      logger.error('❌ Real-time listener error:', error);
      setIsLoadingProducts(false);
    });

    // Cleanup listeners on unmount
    return () => {
      logger.log('🧹 Customer Dashboard: Cleaning up real-time listeners');
      unsubscribeCustomerProducts();
      unsubscribeAllProducts();
    };
  }, []);

  // Load customer product requests from Firestore
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      logger.log('⚠️ No user, skipping product requests load');
      setIsLoadingRequests(false);
      return;
    }

    logger.log('🔍 Loading product requests for customer:', {
      uid: currentUser.uid,
      email: currentUser.email
    });

    setIsLoadingRequests(true);

    // Set up real-time listener for customer's product requests
    const requestsQuery = query(
      collection(db, 'productRequests'),
      where('customerId', '==', currentUser.uid)
    );

    const unsubscribeRequests = onSnapshot(
      requestsQuery,
      (snapshot) => {
        try {
          logger.log('📊 Product requests snapshot received:', snapshot.docs.length, 'documents');
          
          const requests = snapshot.docs.map(doc => {
            const data = doc.data();
            logger.log('📋 Processing request:', {
              id: doc.id,
              productName: data.productName,
              status: data.status,
              customerId: data.customerId
            });
            return {
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(),
              updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : new Date()
            };
          }) as ProductRequest[];
          
          // Sort by createdAt (newest first)
          requests.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          
          logger.log('✅ Setting product requests:', requests.length);
          setProductRequests(requests);
        } catch (error) {
          logger.error('❌ Error processing product requests:', error);
        } finally {
          setIsLoadingRequests(false);
        }
      },
      (error) => {
        logger.error('❌ Error loading product requests:', error);
        logger.error('❌ Error details:', {
          message: error?.message,
          code: error?.code,
          stack: error?.stack
        });
        setIsLoadingRequests(false);
      }
    );

    const quotesQuery = query(
      collection(db, 'quoteRequests'),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribeQuotes = onSnapshot(
      quotesQuery,
      (snapshot) => {
        const quotes = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            rateOfferedAt: data.rateOfferedAt?.toDate ? data.rateOfferedAt.toDate() : data.rateOfferedAt,
            rateRespondedAt: data.rateRespondedAt?.toDate ? data.rateRespondedAt.toDate() : data.rateRespondedAt,
            rateHistory: parseRateHistory(data.rateHistory),
          } as QuoteRequest;
        });
        quotes.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setQuoteRequests(quotes);
      },
      (error) => {
        logger.error('Error loading quote requests:', error);
      }
    );

    return () => {
      logger.log('🧹 Cleaning up product requests listener');
      unsubscribeRequests();
      unsubscribeQuotes();
    };
  }, []);

  const handleViewDetails = (system: any) => {
    setSelectedSystem(system);
    setShowDetailsModal(true);
  };

  const handleManage = (system: any) => {
    setSelectedSystem(system);
    setShowManageModal(true);
  };

  const handleManageRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('Authentication Required', 'You must be logged in to submit a request');
        return;
      }

      // Save installation request to Firestore
      await saveProductRequest({
        customerId: currentUser.uid,
        customerEmail: currentUser.email || '',
        customerName: currentUser.displayName || 'Unknown Customer',
        productId: selectedSystem?.id || '',
        productName: selectedSystem?.name || 'Unknown Product',
        requestType: 'installation',
        description: `${manageRequest.type}: ${manageRequest.description}`,
        urgency: manageRequest.urgency as 'low' | 'normal' | 'high',
        status: 'pending'
      });

      toast.success('Request Submitted', `Installation request submitted for ${selectedSystem?.name}. Our team will contact you within 24 hours.`);
      setShowManageModal(false);
      setManageRequest({ type: '', description: '', urgency: 'normal' });
    } catch (error) {
      logger.error('Error submitting installation request:', error);
      toast.error('Request Failed', 'Failed to submit installation request. Please try again.');
    }
  };

  const handleManageRequestChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setManageRequest(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductRequest = async (product: any) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('Authentication Required', 'You must be logged in to request a product');
        return;
      }

      // Save product request to Firestore
      await saveProductRequest({
        customerId: currentUser.uid,
        customerEmail: currentUser.email || '',
        customerName: currentUser.displayName || 'Unknown Customer',
        productId: product.id,
        productName: product.name,
        requestType: 'product_request',
        description: `Customer is interested in ${product.name} for their business`,
        urgency: 'normal',
        status: 'pending'
      });

      toast.success('Request Submitted', `Product request submitted for ${product.name}. Our team will contact you within 24 hours to discuss pricing and installation.`);
      setShowProductRequestModal(false);
    } catch (error) {
      logger.error('Error submitting product request:', error);
      toast.error('Request Failed', 'Failed to submit product request. Please try again.');
    }
  };

  // Get customer data from userData state
  const customerData = userData ? {
    businessName: userData.businessName || '',
    contactName: `${userData.firstName} ${userData.lastName}`.trim() || 'Customer',
    email: userData.email || '',
    phone: userData.phone || '',
    accountSince: userData.createdAt ? userData.createdAt.toLocaleDateString() : 'N/A'
  } : {
    businessName: '',
    contactName: 'Loading...',
    email: '',
    phone: '',
    accountSince: 'N/A'
  };
  const displayInitials = customerData.contactName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'C';

  // Empty notifications - can be populated from Firestore later if needed
  const notifications: any[] = [];

  // Load customer documents from Firestore
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsLoadingDocuments(false);
      return;
    }

    logger.log('🔍 Loading documents for customer:', currentUser.uid);
    setIsLoadingDocuments(true);

    // Set up real-time listener for customer's documents
    const documentsQuery = query(
      collection(db, 'documents'),
      where('customerId', '==', currentUser.uid)
    );

    const unsubscribeDocuments = onSnapshot(
      documentsQuery,
      (snapshot) => {
        try {
          logger.log('📊 Documents snapshot received:', snapshot.docs.length, 'documents');
          
          const docs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            uploadedAt: doc.data().uploadedAt?.toDate ? doc.data().uploadedAt.toDate() : new Date()
          })) as Document[];
          
          // Sort by uploadedAt (newest first)
          docs.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
          
          logger.log('✅ Setting documents:', docs.length);
          setDocuments(docs);
        } catch (error) {
          logger.error('❌ Error processing documents:', error);
        } finally {
          setIsLoadingDocuments(false);
        }
      },
      (error) => {
        logger.error('❌ Error loading documents:', error);
        setIsLoadingDocuments(false);
      }
    );

    return () => {
      logger.log('🧹 Cleaning up documents listener');
      unsubscribeDocuments();
    };
  }, []);


  // Available products that customers can request - now loaded from Firestore
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const pendingRateOffers = quoteRequests.filter((quote) => quote.rateStatus === 'offered').length;

  const handleRespondToRate = async (quoteId: string, accepted: boolean) => {
    setRespondingQuoteId(quoteId);
    try {
      await respondToQuoteRate(quoteId, accepted);
      toast.success(
        accepted ? 'Rate accepted' : 'Rate declined',
        accepted
          ? 'We will follow up to complete your setup.'
          : 'You can request another rate if you want us to try again.'
      );
    } catch (error) {
      logger.error('Error responding to rate:', error);
      toast.error('Could not update rate', 'Please try again.');
    } finally {
      setRespondingQuoteId(null);
    }
  };

  const handleRequestAnotherRate = async (quoteId: string) => {
    setRespondingQuoteId(quoteId);
    try {
      await requestAnotherRate(quoteId);
      toast.success('New rate requested', 'We will send another rate you can review.');
    } catch (error) {
      logger.error('Error requesting another rate:', error);
      toast.error('Could not request a new rate', 'Please try again.');
    } finally {
      setRespondingQuoteId(null);
    }
  };

  const quoteStatusLabel = (quote: QuoteRequest) => {
    if (quote.rateStatus === 'offered') return 'Rate ready';
    if (quote.rateStatus === 'accepted') return 'Rate accepted';
    if (quote.rateStatus === 'declined') return 'Rate declined';
    if (quote.rateStatus === 'revision_requested') return 'New rate requested';
    if (quote.status === 'in-progress') return 'In review';
    return quote.status.replace('-', ' ');
  };

  const quoteStatusColor = (quote: QuoteRequest) => {
    if (quote.rateStatus === 'offered') return 'bg-blue-100 text-blue-800';
    if (quote.rateStatus === 'accepted' || quote.status === 'completed') return 'bg-green-100 text-green-800';
    if (quote.rateStatus === 'revision_requested') return 'bg-purple-100 text-purple-800';
    if (quote.rateStatus === 'declined' || quote.status === 'declined') return 'bg-orange-100 text-orange-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Package },
    { id: 'systems', name: 'My Products', icon: CreditCard },
    {
      id: 'requests',
      name: 'My Requests',
      icon: Send,
      ...(pendingRateOffers > 0 && { badge: pendingRateOffers }),
    },
    { 
      id: 'notifications', 
      name: 'Notifications', 
      icon: Bell, 
      ...(unreadNotifications > 0 && { badge: unreadNotifications })
    },
    { id: 'documents', name: 'Documents', icon: FileText },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];
  const activeTabMeta = tabs.find((tab) => tab.id === activeTab);

  const totalCustomerRequests = quoteRequests.length + productRequests.filter((request) => !request.description?.startsWith('Quote request')).length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
      case 'Completed':
      case 'Available':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Inactive':
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Active':
      case 'Completed':
      case 'Available':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Inactive':
      case 'Failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const renderOverview = () => (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg p-4 sm:p-5 md:p-6">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">Welcome back, {customerData.contactName}!</h2>
        <p className="text-xs sm:text-sm md:text-base text-gray-100">Here's what's happening with your payment processing</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow-sm sm:shadow p-4 sm:p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Your Products</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">{customerProducts.length}</p>
              <p className="text-xs sm:text-sm text-gray-600">
                {customerProducts.length === 0 ? 'None assigned yet' : 'Assigned to your account'}
              </p>
            </div>
            <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 flex-shrink-0 ml-2" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm sm:shadow p-4 sm:p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Unread Messages</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">{unreadNotifications}</p>
              <p className="text-xs sm:text-sm text-gray-600">From admin</p>
            </div>
            <Bell className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 flex-shrink-0 ml-2" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm sm:shadow p-4 sm:p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600">My Requests</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">{totalCustomerRequests}</p>
              <p className="text-xs sm:text-sm text-gray-600">
                {pendingRateOffers > 0 ? `${pendingRateOffers} rate${pendingRateOffers === 1 ? '' : 's'} to review` : 'Quotes and devices'}
              </p>
            </div>
            <Send className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600 flex-shrink-0 ml-2" />
          </div>
        </div>
      </div>

      {/* Recent Notifications */}
      <div className="bg-white rounded-lg shadow-sm sm:shadow">
        <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Messages</h3>
        </div>
        <div className="p-4 sm:p-5 md:p-6">
          <div className="space-y-4">
            {notifications.slice(0, 3).map((notification) => (
              <div key={notification.id} className={`flex items-start space-x-4 p-4 rounded-lg ${!notification.read ? 'bg-gray-50 border-l-4 border-gray-400' : 'bg-gray-50'}`}>
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    notification.type === 'info' ? 'bg-gray-100' :
                    notification.type === 'success' ? 'bg-green-100' :
                    notification.type === 'warning' ? 'bg-yellow-100' : 'bg-gray-100'
                  }`}>
                    {notification.type === 'info' && <Bell className="h-4 w-4 text-gray-600" />}
                    {notification.type === 'success' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    {notification.type === 'warning' && <AlertCircle className="h-4 w-4 text-yellow-600" />}
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{notification.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                  <p className="text-xs text-gray-500 mt-2">{new Date(notification.date).toLocaleDateString()}</p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderProducts = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Your Products</h2>
        <button 
          onClick={() => setShowProductRequestModal(true)}
          className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300"
        >
          Request New Product
        </button>
      </div>

      {isLoadingProducts ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
          <span className="ml-3 text-gray-600">Loading your products...</span>
        </div>
      ) : customerProducts.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products yet</h3>
          <p className="text-gray-600 mb-4">You haven't been assigned any products yet</p>
          <button 
            onClick={() => setShowProductRequestModal(true)}
            className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300"
          >
            Request Your First Product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {customerProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              {/* Product Image */}
              <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-50 flex items-center justify-center">
                {product.image.startsWith('data:image/') ? (
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : product.image.startsWith('/') ? (
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <div className="text-6xl">{product.image}</div>
                )}
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor('Active')}`}>
                    {getStatusIcon('Active')}
                    Active
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Type:</span>
                    <span className="text-sm font-medium text-gray-900">{product.industry}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Price:</span>
                    <span className="text-sm font-medium text-gray-900">{product.price}</span>
                  </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Installation Date:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {product.assignedAt ? new Date(product.assignedAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button 
                    onClick={() => handleViewDetails(product)}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => handleManage(product)}
                    className="flex-1 bg-gray-600 text-white px-3 py-2 rounded text-sm hover:bg-gray-700 transition-colors"
                  >
                    Manage
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );



  const renderNotifications = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Messages from Admin</h2>
        <button className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300">
          Mark All as Read
        </button>
      </div>

      <div className="space-y-4">
        {notifications.map((notification) => (
          <div key={notification.id} className={`bg-white rounded-lg shadow p-6 border-l-4 ${
            !notification.read ? 'border-gray-400 bg-gray-50' : 'border-gray-200'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  notification.type === 'info' ? 'bg-gray-100' :
                  notification.type === 'success' ? 'bg-green-100' :
                  notification.type === 'warning' ? 'bg-yellow-100' : 'bg-gray-100'
                }`}>
                  {notification.type === 'info' && <Bell className="h-5 w-5 text-gray-600" />}
                  {notification.type === 'success' && <CheckCircle className="h-5 w-5 text-green-600" />}
                  {notification.type === 'warning' && <AlertCircle className="h-5 w-5 text-yellow-600" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900">{notification.title}</h3>
                    {!notification.read && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        New
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mt-2">{notification.message}</p>
                  <p className="text-sm text-gray-500 mt-3">
                    <Clock className="h-4 w-4 inline mr-1" />
                    {new Date(notification.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setShowUploadModal(true);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('No File Selected', 'Please select a file to upload');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      toast.error('Authentication Required', 'You must be logged in to upload documents');
      return;
    }

    setUploading(true);
    try {
      await uploadDocument(
        selectedFile,
        currentUser.uid,
        currentUser.email || '',
        currentUser.displayName || 'Unknown Customer',
        documentDescription
      );

      toast.success('Document Uploaded', 'Your document has been uploaded successfully and is now visible to admin.');
      setShowUploadModal(false);
      setSelectedFile(null);
      setDocumentDescription('');
    } catch (error: any) {
      logger.error('Error uploading document:', error);
      toast.error('Upload Failed', error.message || 'Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = (downloadUrl: string, fileName: string) => {
    window.open(downloadUrl, '_blank');
  };

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Important Documents</h2>
        <label className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 cursor-pointer">
          <input
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            className="hidden"
          />
          Upload Document
        </label>
      </div>

      {isLoadingDocuments ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents yet</h3>
          <p className="text-gray-600 mb-6">Upload your first document to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">{doc.fileName}</h3>
                    <p className="text-xs text-gray-500">{doc.fileType.split('/')[1]?.toUpperCase() || 'FILE'}</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleDownloadDocument(doc.downloadUrl, doc.fileName)}
                  className="text-gray-400 hover:text-gray-600"
                  title="Download"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              </div>
              
              {doc.description && (
                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{doc.description}</p>
              )}
              
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Size: {formatFileSize(doc.fileSize)}</span>
                  <span>{doc.uploadedAt.toLocaleDateString()}</span>
                </div>
                <button 
                  onClick={() => handleDownloadDocument(doc.downloadUrl, doc.fileName)}
                  className="w-full bg-gradient-to-r from-gray-800 to-gray-900 text-white py-2 px-4 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Upload Document</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setDocumentDescription('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selected File</label>
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <FileText className="h-5 w-5 text-gray-600" />
                  <span className="text-sm text-gray-900 truncate flex-1">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={documentDescription}
                  onChange={(e) => setDocumentDescription(e.target.value)}
                  placeholder="Add a description for this document..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  rows={3}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setDocumentDescription('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadDocument}
                  disabled={uploading}
                  className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderRequests = () => (
    <>
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">My Requests</h2>
      </div>

      {isLoadingRequests ? (
        <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading your requests...</p>
        </div>
      ) : quoteRequests.length === 0 && productRequests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
          <Send className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No requests yet</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            You haven't submitted any product requests yet.
          </p>
          <button
            onClick={() => setActiveTab('systems')}
            className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-2 px-4 sm:px-6 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 text-sm sm:text-base"
          >
            Browse Products
          </button>
        </div>
      ) : (
        <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
          {quoteRequests
            .slice()
            .sort((a, b) => (b.rateOfferedAt?.getTime() || b.createdAt.getTime()) - (a.rateOfferedAt?.getTime() || a.createdAt.getTime()))
            .filter((quote, index, list) => {
              const storeQuote = list.find((item) => item.rateCards) || list[0];
              return quote.id === storeQuote.id;
            })
            .map((quote) => (
            <div
              key={`quote-${quote.id}`}
              className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow w-full"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2 sm:mb-3">
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
                    {quote.businessName || 'Store rate'}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Store processing rate
                    {quote.productName || quote.systemType ? ` · Inquiry: ${quote.productName || quote.systemType}` : ''}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium self-start capitalize ${quoteStatusColor(quote)}`}>
                  {quoteStatusLabel(quote)}
                </span>
              </div>

              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mb-3">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>Requested: {quote.createdAt.toLocaleDateString()}</span>
              </div>

              {quote.rateCards ? (
                <div className="mt-3 rounded-lg border border-gray-200 p-3 sm:p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    {quote.rateType === 'ic_plus' ? 'I/C plus' : 'Fixed'} store rate
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    {quote.rateStatus === 'offered'
                      ? 'Review this store rate and accept or decline it.'
                      : quote.rateStatus === 'accepted'
                      ? 'This is your current store rate. Simpl can update it during the year.'
                      : quote.rateStatus === 'declined'
                      ? 'You declined this store rate. Request another if you want us to try again.'
                      : quote.rateStatus === 'revision_requested'
                      ? 'You asked for another store rate. We will send a new one soon.'
                      : 'Assigned store processing rate'}
                  </p>
                  <div className="space-y-2">
                    {CARD_BRANDS.map((brand) => (
                      <div
                        key={brand.id}
                        className="flex items-center justify-between text-sm text-gray-700"
                      >
                        <span>{brand.label}</span>
                        <span className="font-medium">
                          {quote.rateCards?.[brand.id].percent ?? 0}% + $
                          {(quote.rateCards?.[brand.id].fee ?? 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  {quote.rateStatus === 'offered' && (
                    <div className="mt-4 flex gap-2">
                      <button
                        type="button"
                        disabled={respondingQuoteId === quote.id}
                        onClick={() => handleRespondToRate(quote.id, true)}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                      >
                        {respondingQuoteId === quote.id ? 'Saving...' : 'Accept rate'}
                      </button>
                      <button
                        type="button"
                        disabled={respondingQuoteId === quote.id}
                        onClick={() => setDeclineQuote(quote)}
                        className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                      >
                        Decline
                      </button>
                    </div>
                  )}
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const offers = getRateOffers(quote);
                        const latest = offers[offers.length - 1];
                        if (latest) downloadRatePdf(quote, latest, offers.length);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                    {quote.rateStatus === 'declined' && (
                      <button
                        type="button"
                        disabled={respondingQuoteId === quote.id}
                        onClick={() => handleRequestAnotherRate(quote.id)}
                        className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black disabled:opacity-50 text-sm"
                      >
                        {respondingQuoteId === quote.id ? 'Sending...' : 'Request another rate'}
                      </button>
                    )}
                  </div>
                  <RateHistory quote={quote} pastOnly />
                </div>
              ) : (
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  Waiting for Simpl to assign your processing rate.
                </p>
              )}
            </div>
          ))}

          {quoteRequests
            .slice()
            .sort((a, b) => (b.rateOfferedAt?.getTime() || b.createdAt.getTime()) - (a.rateOfferedAt?.getTime() || a.createdAt.getTime()))
            .filter((quote, _index, list) => {
              const storeQuote = list.find((item) => item.rateCards) || list[0];
              return quote.id !== storeQuote.id;
            })
            .map((quote) => (
              <div
                key={`quote-inquiry-${quote.id}`}
                className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6 w-full"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                      {quote.productName || quote.systemType || 'Product inquiry'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">Uses the store rate for {quote.businessName}</p>
                  </div>
                  <span className="text-xs text-gray-500">{quote.createdAt.toLocaleDateString()}</span>
                </div>
              </div>
            ))}

          {productRequests
            .filter((request) => !request.description?.startsWith('Quote request'))
            .map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow w-full"
            >
              <div className="w-full">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2 sm:mb-3">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 flex-1">
                    {request.productName}
                  </h3>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium self-start ${getStatusColor(
                      request.status
                    )}`}
                  >
                    {request.status.charAt(0).toUpperCase() +
                      request.status.slice(1).replace('-', ' ')}
                  </span>
                </div>

                <div className="flex flex-col gap-2 sm:gap-2.5 mt-2 sm:mt-3">
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span>Requested: {request.createdAt.toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                    <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span>
                      Type:{' '}
                      {request.requestType === 'product_request'
                        ? 'Product Request'
                        : 'Installation'}
                    </span>
                  </div>
                  {request.urgency && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                      <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span>
                        Urgency:{' '}
                        {request.urgency.charAt(0).toUpperCase() + request.urgency.slice(1)}
                      </span>
                    </div>
                  )}
                </div>

                {request.description && (
                  <div className="mt-2.5 sm:mt-3 md:mt-4">
                    <p className="text-xs sm:text-sm text-gray-700 bg-gray-50 p-2.5 sm:p-3 rounded-lg break-words">
                      {request.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {declineQuote && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Before you decline</h3>
            <button
              type="button"
              onClick={() => setDeclineQuote(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            Anything you do not like about this rate? Contact us and we will work with you — we can adjust it so it fits your business.
          </p>
          <div className="space-y-2 mb-5">
            <a
              href="tel:+12018460591"
              className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50"
            >
              <Phone className="h-4 w-4 text-gray-700" />
              <div>
                <p className="text-sm font-medium text-gray-900">Call us</p>
                <p className="text-sm text-gray-600">(201) 846-0591</p>
              </div>
            </a>
            <a
              href="mailto:simran@simplprocessing.com?subject=Rate%20question"
              className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 hover:bg-gray-50"
            >
              <Mail className="h-4 w-4 text-gray-700" />
              <div>
                <p className="text-sm font-medium text-gray-900">Email us</p>
                <p className="text-sm text-gray-600">simran@simplprocessing.com</p>
              </div>
            </a>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDeclineQuote(null)}
              className="flex-1 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black text-sm"
            >
              Keep reviewing
            </button>
            <button
              type="button"
              disabled={respondingQuoteId === declineQuote.id}
              onClick={async () => {
                await handleRespondToRate(declineQuote.id, false);
                setDeclineQuote(null);
              }}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
            >
              {respondingQuoteId === declineQuote.id ? 'Saving...' : 'Decline anyway'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
      
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
            <input
              type="text"
              value={customerData.businessName}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
            <input
              type="text"
              value={customerData.contactName}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={customerData.email}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={customerData.phone}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
        </div>
        <button className="mt-4 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
          Save Changes
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Support</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <Phone className="h-5 w-5 text-gray-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Phone Support</p>
                <p className="text-sm text-gray-600">(201) 846-0591</p>
              </div>
            </div>
            <button className="text-gray-600 hover:text-gray-800">Call Now</button>
          </div>
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-gray-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Email Support</p>
                <p className="text-sm text-gray-600">simran@simplprocessing.com</p>
              </div>
            </div>
            <button className="text-gray-600 hover:text-gray-800">Send Email</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'systems':
        return renderProducts();
      case 'requests':
        return renderRequests();
      case 'notifications':
        return renderNotifications();
      case 'documents':
        return renderDocuments();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  const { ToastContainer } = toast;

  const renderSidebar = () => (
    <div className="flex h-full flex-col">
      <div className="px-5 pt-6 pb-5">
        <button type="button" onClick={handleGoHome} className="flex items-center">
          <img src="/logo.png?v=1" alt="Simpl Processing" className="h-8 w-auto" />
        </button>
      </div>

      <div className="px-5 pb-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
            {displayInitials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{customerData.contactName}</p>
            <p className="text-xs text-gray-500 truncate">{customerData.email}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => {
              setActiveTab('settings');
              setMobileMenuOpen(false);
            }}
            className="text-gray-500 hover:text-gray-900"
          >
            Profile
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={handleLogout}
            className="text-gray-500 hover:text-red-600"
          >
            Logout
          </button>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3" aria-label="Customer">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
                setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 mb-0.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <tab.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-gray-900' : 'text-gray-400'}`} />
              <span className="flex-1 text-left">{tab.name}</span>
              {tab.badge && tab.badge > 0 && (
                <span className="bg-red-500 text-white text-[10px] rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="px-5 py-4 border-t border-gray-100">
        <button
          type="button"
          onClick={handleGoHome}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900"
        >
          <Home className="h-3.5 w-3.5" />
          Back to website
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f4f5f7] flex">
      <ToastContainer />
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-gray-200 sticky top-0 h-screen">
        {renderSidebar()}
      </aside>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} aria-hidden />
          <aside className="relative z-50 h-full w-72 max-w-[85vw] bg-white shadow-xl">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-700 rounded-lg"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            {renderSidebar()}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3 sticky top-0 z-30">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-50 rounded-lg"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {activeTabMeta?.name || 'Dashboard'}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">Welcome back, {customerData.contactName}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="ml-auto hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </header>

        <main className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          {renderTabContent()}
        </main>
      </div>

      {/* View Details Modal */}
      {showDetailsModal && selectedSystem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedSystem.name} Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Product Image */}
                <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-50 flex items-center justify-center rounded-lg">
                  {selectedSystem.image === '/poynt-c.png' ? (
                    <img 
                      src={selectedSystem.image} 
                      alt={selectedSystem.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <div className="text-6xl">{selectedSystem.image}</div>
                  )}
                </div>

                {/* Product Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Name:</span>
                        <span className="text-sm font-medium text-gray-900">{selectedSystem.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Type:</span>
                        <span className="text-sm font-medium text-gray-900">{selectedSystem.type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status:</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedSystem.status)}`}>
                          {getStatusIcon(selectedSystem.status)}
                          {selectedSystem.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Installation Date:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(selectedSystem.installationDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {selectedSystem.description}
                    </p>
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-end pt-6">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Request Modal */}
      {showManageModal && selectedSystem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Request Installation Service</h2>
                <button
                  onClick={() => setShowManageModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-800">
                  <strong>Product:</strong> {selectedSystem.name}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Submit a request for installation, maintenance, or support services.
                </p>
              </div>

              <form onSubmit={handleManageRequestSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request Type *
                  </label>
                  <select
                    name="type"
                    value={manageRequest.type}
                    onChange={handleManageRequestChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  >
                    <option value="">Select request type</option>
                    <option value="installation">New Installation</option>
                    <option value="maintenance">Maintenance/Repair</option>
                    <option value="training">Training/Support</option>
                    <option value="upgrade">System Upgrade</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Urgency Level
                  </label>
                  <select
                    name="urgency"
                    value={manageRequest.urgency}
                    onChange={handleManageRequestChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  >
                    <option value="low">Low - Can wait a few days</option>
                    <option value="normal">Normal - Within 24-48 hours</option>
                    <option value="high">High - Same day</option>
                    <option value="urgent">Urgent - Immediate attention</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={manageRequest.description}
                    onChange={handleManageRequestChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="Please describe your request in detail..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowManageModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                  >
                    Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Product Request Modal */}
      {showProductRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Available Products</h2>
                <button
                  onClick={() => setShowProductRequestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-800">
                  <strong>Request a Product:</strong> Browse our available POS systems and request installation for your business.
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Our team will contact you within 24 hours to discuss pricing and setup.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {availableProducts.map((product) => (
                  <div key={product.id} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
                    {/* Product Image */}
                    <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-50 flex items-center justify-center">
                      {product.image.startsWith('data:image/') ? (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="max-h-full max-w-full object-contain"
                          onError={(e) => {
                            logger.error('Image failed to load:', product.name, product.image.substring(0, 100) + '...');
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : product.image.startsWith('/') ? (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div className="text-6xl">{product.image}</div>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                          {product.type}
                        </span>
                      </div>

                      <p className="text-gray-600 text-sm mb-4">{product.description}</p>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl font-bold text-gray-600">{product.price}</span>
                          {product.originalPrice && (
                            <span className="text-sm text-gray-500 line-through">{product.originalPrice}</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="text-sm text-gray-600">{product.rating}</span>
                          <span className="text-sm text-gray-500">({product.reviews})</span>
                        </div>
                      </div>

                      {/* Key Features */}
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Key Features:</h4>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {(product.features || []).slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                          {(product.features || []).length > 3 && (
                            <li className="text-gray-500">+{(product.features || []).length - 3} more features</li>
                          )}
                        </ul>
                      </div>

                      {/* Request Button */}
                      <button
                        onClick={() => handleProductRequest(product)}
                        className="w-full bg-gradient-to-r from-gray-800 to-gray-900 text-white py-2 px-4 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <Package className="h-4 w-4" />
                        Request This Product
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-6">
                <button
                  onClick={() => setShowProductRequestModal(false)}
                  className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
